import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import {
  paymentMethodExpiry,
  paymentMethodLabel,
  paymentMethodWarning,
} from '../../account/payment-method'
import { messageForBillingError } from '../../api/errors'
import { requestPaymentMethodUpdateEmail } from '../../api/payment-method-update-emails'
import type { PaymentMethod } from '../../api/shipping-addresses'
import type { Subscription } from '../../api/subscriptions'
import { firstName, possessive, type User } from '../../api/users'
import { currentAuth, useSession } from '../../state/session'
import { Button } from '../ui/Button'
import { Dialog } from '../ui/Dialog'
import { TextButton } from '../ui/TextButton'
import { SavedIndicator, SettingRow, ValueSkeleton } from './Setting'

interface PaymentSettingProps {
  subscription: Subscription
  child: User
  /** What the subscription's address is charged to; null when Recharge has nothing yet. */
  method: PaymentMethod | null
  /** Whether the address book is still loading. */
  pending: boolean
}

/**
 * The card behind the subscription, and the way to change it. Shopify keeps
 * the card and hosts the page that changes it, so the button asks for the
 * email that carries the link and then explains what to do with it — the
 * parent asked to update a card, and is told how.
 */
export function PaymentSetting({ subscription, child, method, pending }: PaymentSettingProps) {
  const email = useSession((state) => state.user?.email ?? null)
  const [explaining, setExplaining] = useState(false)
  const send = useMutation({
    mutationFn: () => requestPaymentMethodUpdateEmail(subscription.id, { auth: currentAuth() }),
    onSuccess: () => setExplaining(true),
  })

  // The buttons stay focusable while the email is asked for, marked
  // aria-disabled rather than disabled: a browser drops focus from a disabled
  // element, and the dialog needs to find the button to hand focus back to.
  function ask() {
    if (!send.isPending) send.mutate()
  }

  const verb = method ? 'Update' : 'Add'
  const expiry = method && paymentMethodExpiry(method)

  return (
    <>
      <SettingRow
        label="Payment"
        value={
          pending ? (
            <ValueSkeleton />
          ) : method ? (
            `${paymentMethodLabel(method)}${expiry ? ` · expires ${expiry}` : ''}`
          ) : (
            'No payment method on file'
          )
        }
        warning={method && paymentMethodWarning(method)}
        error={send.isError ? messageForBillingError(send.error) : null}
        action={
          !pending && (
            <>
              {send.isSuccess && <SavedIndicator>Sent</SavedIndicator>}
              <Button
                variant="secondary"
                onClick={ask}
                aria-disabled={send.isPending || undefined}
                aria-label={`${verb} the payment method for ${possessive(firstName(child))} subscription`}
                data-testid="payment-update"
              >
                {send.isPending ? 'Sending…' : verb}
              </Button>
            </>
          )
        }
        testId="setting-payment"
      />

      <Dialog
        open={explaining}
        onClose={() => setExplaining(false)}
        title="Check your email"
        testId="payment-dialog"
      >
        <p className="mt-3 text-base text-ink">
          We’ve sent a secure link to {email ? <strong>{email}</strong> : 'your email address'}.
        </p>
        <p className="mt-3 text-sm text-ink-soft">
          Shopify handles payments for Codeverse, so cards are updated on their secure page. Your
          card details never pass through this site.
        </p>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-base text-ink">
          <li>Open the email from Shopify. It should arrive within a few minutes.</li>
          <li>Follow the link and enter your new card.</li>
          <li>
            That’s it. The new card is used for the next charge, and for any of your kids’
            subscriptions billed to the same card.
          </li>
        </ol>
        <p className="mt-4 text-sm text-ink-soft">
          Didn’t get it? Check your spam folder, or{' '}
          <TextButton
            onClick={ask}
            aria-disabled={send.isPending || undefined}
            data-testid="payment-resend"
          >
            {send.isPending ? 'sending…' : 'send it again'}
          </TextButton>
          .
        </p>
        {send.isError && (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {messageForBillingError(send.error)}
          </p>
        )}
        <div className="mt-6">
          <Button
            variant="compact"
            onClick={() => setExplaining(false)}
            data-testid="payment-dialog-done"
          >
            Done
          </Button>
        </div>
      </Dialog>
    </>
  )
}
